import React, { useState, useEffect } from 'react';
import { api, endpoints } from '../config/api';
import { SITE_ORIGIN } from '../config/site';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { FcGoogle } from 'react-icons/fc';
import { MdEmail } from 'react-icons/md';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { hashPassword } from '../utils/security';
import { Helmet } from 'react-helmet-async';
import ReactGA from 'react-ga4';
import { analytics } from '../utils/analytics';
import { getJsonLdLanguage } from '../utils/seoUtils';

const Login = ({ onAuthSuccess, onLoginSuccess }) => {
    const { t } = useTranslation();
    const { currentLanguage } = useLanguage();
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [step, setStep] = useState('email');
    const [isInitializing, setIsInitializing] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [isGoogleUser, setIsGoogleUser] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const error = params.get('error');
        const encodedEmail = params.get('email');
        const message = params.get('message');
        const justLoggedOut = location.state?.justLoggedOut;

        if (error && !justLoggedOut) {
            switch (error) {
                case 'email_exists':
                    const decodedEmail = encodedEmail ? decodeURIComponent(encodedEmail) : '';
                    setEmail(decodedEmail);
                    setError({
                        type: 'info',
                        message: message || t('auth.emailExists')
                    });
                    break;
                case 'auth_failed':
                    setError({
                        type: 'error',
                        message: t('auth.googleAuthFailed')
                    });
                    break;
                case 'server_error':
                    setError({
                        type: 'error',
                        message: t('auth.serverError')
                    });
                    break;
                default:
                    setError({
                        type: 'error',
                        message: t('auth.unknownError')
                    });
            }
        }
    }, [location, t]);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const openSubscription = searchParams.get('openSubscription') === 'true';
        
        if (openSubscription) {
            sessionStorage.setItem('postLoginRedirect', '/task-manager?openSubscription=true');
        }
    }, [location]);

    useEffect(() => {
        const checkAuth = async () => {
            const token = localStorage.getItem('authToken');
            if (token && !location.state?.justLoggedOut) {
                try {
                    const response = await api.get(endpoints.profile.get);
                    if (response.data.success) {
                        const params = new URLSearchParams(window.location.search);
                        const redirectUrl = params.get('redirect') || 
                                         sessionStorage.getItem('postLoginRedirect') ||
                                         '/task-manager';
                        
                        sessionStorage.removeItem('postLoginRedirect');
                        navigate(redirectUrl, { replace: true });
                        return;
                    }
                } catch (error) {
                    if (error.response?.status === 401 || error.response?.status === 403) {
                        localStorage.removeItem('authToken');
                        localStorage.removeItem('userId');
                    }
                }
            }
            setIsInitializing(false);
        };

        checkAuth();
    }, [navigate, location.state]);

    // Add Google Analytics page view tracking
    useEffect(() => {
        // Track page view
        ReactGA.send({ 
            hitType: "pageview", 
            page: "/login" + window.location.search,
            title: "Login Page"
        });
    }, []);

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError({
                type: 'error',
                message: t('auth.invalidEmail')
            });
            return false;
        }
        return true;
    };

    const handleEmailCheck = async (e) => {
        e.preventDefault();
        if (!validateEmail(email)) return;
        
        setError('');
        setIsSubmitting(true);

        try {
            const checkResponse = await api.post(endpoints.auth.checkEmail, { email });
            const { exists, isEmailVerified, isGoogleUser } = checkResponse.data;
            
            if (exists && isGoogleUser) {
                setIsGoogleUser(true);
                setError({
                    type: 'info',
                    message: t('auth.useGoogleLogin')
                });
                setIsSubmitting(false);
                return;
            }
            
            // Case 1: Existing verified user - show password prompt
            if (exists && isEmailVerified) {
                setStep('password');
                setIsSubmitting(false);
                return;
            }
            
            // Case 2: Existing unverified user - resend verification
            if (exists && !isEmailVerified) {
                try {
                    const resendResponse = await api.post(endpoints.auth.resendVerification, { email });
                    if (resendResponse.data.success) {
                        navigate('/email-confirmation', { 
                            state: { 
                                email, 
                                isNewUser: false,
                                message: t('auth.verificationResent')
                            },
                            replace: true 
                        });
                    }
                } catch (resendError) {
                    console.error('Resend verification error:', resendError);
                    setError({
                        type: 'error',
                        message: resendError.response?.data?.message || t('auth.serverError')
                    });
                }
                setIsSubmitting(false);
                return;
            }
            
            // Case 3: New user - register and send verification
            const registerResponse = await api.post(endpoints.auth.register, { email });
            if (registerResponse.data.success) {
                // Track successful signup for affiliate program
                analytics.trackSignUp();
                
                navigate('/email-confirmation', { 
                    state: { 
                        email, 
                        isNewUser: true,
                        message: t('auth.registrationSuccess')
                    },
                    replace: true 
                });
            }
        } catch (error) {
            console.error('Auth error:', error);
            const errorCode = error.response?.data?.error;
            let errorMessage;
            
            switch (errorCode) {
                case 'email_service_error':
                    errorMessage = t('auth.emailServiceError');
                    break;
                case 'email_already_exists':
                    errorMessage = t('auth.userExists');
                    break;
                case 'already_verified':
                    errorMessage = t('auth.emailAlreadyVerified');
                    break;
                case 'user_not_found':
                    errorMessage = t('auth.emailNotFound');
                    break;
                default:
                    errorMessage = t('auth.serverError');
            }
            
            setError({
                type: 'error',
                message: errorMessage
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        if (!validateEmail(email)) return;

        setIsSubmitting(true);
        try {
            const hashedPassword = hashPassword(password);
            const response = await api.post(endpoints.auth.login, {
                email,
                password: hashedPassword
            });

            if (response.data.success) {
                localStorage.setItem('authToken', response.data.token);
                localStorage.setItem('userId', response.data.userId);
                
                // Track successful login
                analytics.trackLogin();
                
                if (onLoginSuccess) {
                    onLoginSuccess(response.data.userId);
                }

                // Check for redirect parameter first
                const params = new URLSearchParams(window.location.search);
                const redirectUrl = params.get('redirect') || 
                                  sessionStorage.getItem('postLoginRedirect') ||
                                  (response.data.hasCompletedWizard ? '/task-manager' : '/wizard');
                
                sessionStorage.removeItem('postLoginRedirect');
                navigate(redirectUrl);
            }
        } catch (error) {
            setError({
                type: 'error',
                message: error.response?.data?.message || t('auth.serverError')
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleGoogleLogin = () => {
        // Clear any existing tokens first
        localStorage.removeItem('authToken');
        localStorage.removeItem('userId');
        
        // Track Google login attempt
        analytics.trackLogin();
        
        // Add state parameter to prevent automatic login
        const state = btoa(JSON.stringify({
            forceLogin: true,
            timestamp: Date.now()
        }));
        
        // Redirect directly to backend domain for Google OAuth
        window.location.href = `https://api.livelovespain.com/api/auth/google?state=${state}`;
    };

    if (isInitializing) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            <Helmet htmlAttributes={{ lang: currentLanguage }}>
                <title>{t('common.login')} - LiveLoveSpain</title>
                <meta 
                    name="description" 
                    content={t('login.description', "Access your personalized Spanish immigration journey with LiveLoveSpain. Track your visa progress, explore cost of living data, and get expert guidance.")}
                />
                <meta 
                    name="keywords" 
                    content="spain immigration login, spanish visa tracking, immigration assistant login, spain relocation tools"
                />
                {/* Open Graph tags for social sharing */}
                <meta property="og:title" content={`${t('common.login')} - LiveLoveSpain`} />
                <meta 
                    property="og:description" 
                    content={t('login.description', "Access your personalized Spanish immigration journey with LiveLoveSpain. Track your visa progress, explore cost of living data, and get expert guidance.")}
                />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={`${SITE_ORIGIN}/login${window.location.search || ''}`} />
                <meta property="og:image" content={`${SITE_ORIGIN}/assets/LiveLoveSpain.webp`} />
                
                {/* Twitter Card tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={`${t('common.login')} - LiveLoveSpain`} />
                <meta 
                    name="twitter:description" 
                    content={t('login.description', "Access your personalized Spanish immigration journey with LiveLoveSpain. Track your visa progress, explore cost of living data, and get expert guidance.")}
                />
                <meta name="twitter:image" content={`${SITE_ORIGIN}/assets/LiveLoveSpain-512.png`} />
                
                {/* Canonical URL */}
                <link key="canonical" rel="canonical" href={`${SITE_ORIGIN}${currentLanguage === 'en' ? '' : `/${currentLanguage}`}/login`} />

                {/* Additional meta tags */}
                <meta name="robots" content="noindex, nofollow" /> {/* Since this is a login page */}
                <meta name="author" content="LiveLoveSpain" />

                {/* Add structured data for better SEO */}
                <script key="ld:page" type="application/ld+json" data-page={`${SITE_ORIGIN}/login`}>
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@graph": [
                            {
                                "@type": "WebPage",
                                "name": "Login - LiveLoveSpain Immigration Assistant",
                                "description": "Access your personalized Spanish immigration journey with LiveLoveSpain. Track your visa progress, explore cost of living data, and get expert guidance.",
                                "publisher": {
                                    "@type": "Organization",
                                    "@id": `${SITE_ORIGIN}/#organization`
                                }
                            },
                            {
                                "@type": "VideoObject",
                                "name": "LiveLoveSpain Immigration Platform - Login Background Demo",
                                "description": "Background demonstration video showing LiveLoveSpain's immigration platform features displayed on the login page. Overview of interactive timeline, task management, cost explorer and AI assistance tools.",
                                "thumbnailUrl": [
                                    `${window.location.origin}/assets/Livelovespain_demo.webp`
                                ],
                                "thumbnail": {
                                    "@type": "ImageObject",
                                    "url": `${window.location.origin}/assets/Livelovespain_demo.webp`,
                                    "width": "1280",
                                    "height": "720"
                                },
                                "uploadDate": "2025-06-18T00:00:00Z",
                                "duration": "PT1M20S",
                                "contentUrl": "https://fjuwnjwmrayamlgy.public.blob.vercel-storage.com/LLS_demo-8PqGbNUrpC9R3bEzLellG7H6DHfAJC.mp4",
                                "embedUrl": `${window.location.origin}/login`,
                                "url": `${window.location.origin}/login`,
                                "publisher": {
                                    "@type": "Organization",
                                    "@id": `${SITE_ORIGIN}/#organization`
                                },
                                "author": {
                                    "@type": "Organization",
                                    "@id": `${SITE_ORIGIN}/#organization`
                                },
                                "keywords": "Spain immigration platform, login, visa tracking, Spanish residency tools",
                                "category": "Education",
                                "isFamilyFriendly": true,
                                "isAccessibleForFree": true,
                                "inLanguage": getJsonLdLanguage(currentLanguage),
                                "license": `${window.location.origin}/terms`,
                                "copyrightHolder": {
                                    "@type": "Organization",
                                    "@id": `${SITE_ORIGIN}/#organization`
                                },
                                "interactionStatistic": {
                                    "@type": "InteractionCounter",
                                    "interactionType": "http://schema.org/WatchAction",
                                    "userInteractionCount": "0"
                                }
                            }
                        ]
                    })}
                </script>

            </Helmet>

            {/* Mobile Banner */}
            <div className="lg:hidden bg-blue-600 text-white">
                <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-b from-blue-500/50 to-blue-700/50 z-10"></div>
                    <div className="relative z-20 px-6 py-8 text-center">
                        <h1 className="text-2xl font-bold mb-2">
                            {t('auth.videoTitle')}
                        </h1>
                        <p className="text-sm opacity-90">
                            {t('auth.videoSubtitle')}
                        </p>
                    </div>
                </div>
            </div>

            <div className="flex flex-1">
                {/* Video Section - hidden on mobile */}
                <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
                    {/* Content overlay - updated positioning and content */}
                    <div className="absolute top-0 left-0 right-0 z-20 p-12 bg-gradient-to-b from-blue-600 to-transparent">
                        <div className="max-w-xl space-y-8">
                            {/* Title and subtitle */}
                            <div>
                                <h1 className="text-4xl font-bold mb-4 text-white drop-shadow-sm">
                                    {t('auth.videoTitle')}
                                </h1>
                                <p className="text-xl text-white/90 drop-shadow-sm mb-6">
                                    {t('auth.videoSubtitle')}
                                </p>
                            </div>

                            {/* Features list */}
                            <div className="grid grid-cols-2 gap-4 text-sm text-white/90">
                                <div className="space-y-3">
                                    <p className="flex items-center gap-2">
                                        <span>{t('welcome.pricing.featuresList.interactiveTimeline')}</span>
                                    </p>
                                    <p className="flex items-center gap-2">
                                        <span>{t('welcome.pricing.featuresList.documentChecklist')}</span>
                                    </p>
                                    <p className="flex items-center gap-2">
                                        <span>{t('welcome.pricing.featuresList.progressDashboard')}</span>
                                    </p>
                                    <p className="flex items-center gap-2">
                                        <span>{t('welcome.pricing.featuresList.cityExplorer')}</span>
                                    </p>
                                    <p className="flex items-center gap-2">
                                        <span>{t('welcome.pricing.featuresList.deadlineReminders')}</span>
                                    </p>
                                </div>
                                <div className="space-y-3">
                                    <p className="flex items-center gap-2">
                                        <span>{t('welcome.pricing.featuresList.requirementsLibrary')}</span>
                                    </p>
                                    <p className="flex items-center gap-2">
                                        <span>{t('welcome.pricing.featuresList.insightsUpdates')}</span>
                                    </p>
                                    <p className="flex items-center gap-2">
                                        <span>{t('welcome.pricing.featuresList.serviceDirectory')}</span>
                                    </p>
                                    <p className="flex items-center gap-2">
                                        <span>{t('welcome.pricing.featuresList.aiAssistant')}</span>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Background color base */}
                    <div className="absolute inset-0 bg-blue-600"></div>
                    
                    {/* Loading placeholder with smoother gradient */}
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 animate-pulse"></div>
                    
                    {/* Video container - updated positioning */}
                    <div className="absolute inset-0 flex items-end justify-center px-12 pb-12">
                        {/* Video element */}
                        <video 
                            className="w-full h-[50%] object-contain opacity-0 transition-opacity duration-700 ease-in-out"
                            autoPlay 
                            loop 
                            muted 
                            playsInline
                            poster="/assets/Livelovespain_demo.webp"
                            onCanPlay={(e) => {
                                e.target.style.opacity = '0.9';
                                const fallbackImg = e.target.parentElement.querySelector('.fallback-image');
                                if (fallbackImg) {
                                    fallbackImg.style.opacity = '0';
                                }
                            }}
                            onError={(e) => {
                                const fallbackImg = e.target.parentElement.querySelector('.fallback-image');
                                if (fallbackImg) {
                                    fallbackImg.style.opacity = '0.9';
                                }
                            }}
                        >
                            <source 
                                src="https://fjuwnjwmrayamlgy.public.blob.vercel-storage.com/LLS_demo-8PqGbNUrpC9R3bEzLellG7H6DHfAJC.mp4" 
                                type="video/mp4"
                            />
                        </video>

                    </div>
                </div>

                {/* Auth Section */}
                <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
                    <div className="w-full max-w-md space-y-8">
                        <div className="text-center">
                            <Link to="/" className="inline-block hover:opacity-90 transition-opacity">
                                <img 
                                    src="/assets/LiveLoveSpain.webp"
                                    alt="LiveLoveSpain Logo"
                                    className="h-24 w-auto mx-auto mb-8"
                                    style={{ maxHeight: '96px' }}
                                />
                            </Link>
                            <h2 className="text-3xl font-bold text-gray-900 mb-2">
                                {step === 'email' ? t('auth.welcome') : t('auth.welcomeBack')}
                            </h2>
                            <p className="text-gray-600">
                                {step === 'email' ? t('auth.subtitle') : t('auth.enterPassword')}
                            </p>
                        </div>

                        <form 
                            onSubmit={step === 'email' ? handleEmailCheck : handleLogin} 
                            className="space-y-6"
                            autoComplete="on"
                        >
                            {isGoogleUser ? (
                                <button
                                    type="button"
                                    onClick={handleGoogleLogin}
                                    className="w-full flex items-center justify-center text-white gap-3 px-4 py-3 bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-md"
                                >
                                    <FcGoogle className="text-2xl" />
                                    <span>{t('auth.signInWithGoogle')}</span>
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={handleGoogleLogin}
                                    className="w-full flex items-center justify-center text-gray-700 gap-3 px-4 py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm hover:shadow-md"
                                >
                                    <FcGoogle className="text-2xl" />
                                    <span>{t('auth.continueWithGoogle')}</span>
                                </button>
                            )}

                            <div className="relative">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-gray-300"></div>
                                </div>
                                <div className="relative flex justify-center text-sm">
                                    <span className="px-4 bg-gray-50 text-gray-500">
                                        {t('auth.orDelimiter')}
                                    </span>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('auth.emailLabel')}
                                    </label>
                                    <input
                                        id="email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        disabled={step === 'password'}
                                        className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                            step === 'password' 
                                                ? 'bg-gray-100 text-gray-500' 
                                                : 'bg-white'
                                        }`}
                                        placeholder={t('auth.emailPlaceholder')}
                                        autoComplete="username email"
                                        name="username"
                                        required
                                    />
                                </div>

                                {step === 'password' && (
                                    <div>
                                        <label htmlFor="current-password" className="block text-sm font-medium text-gray-700 mb-1">
                                            {t('auth.passwordLabel')}
                                        </label>
                                        <input
                                            id="current-password"
                                            type="password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder={t('auth.passwordPlaceholder')}
                                            autoComplete="current-password"
                                            name="password"
                                            required
                                        />
                                    </div>
                                )}
                            </div>

                            {error && (
                                <div className={`p-4 rounded-lg text-sm ${
                                    error.type === 'success' 
                                        ? 'bg-green-50 text-green-700'
                                        : error.type === 'info'
                                        ? 'bg-blue-50 text-blue-700' 
                                        : 'bg-red-50 text-red-500'
                                }`}>
                                    {error.message}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-75 disabled:cursor-not-allowed hover:bg-blue-700"
                            >
                                <MdEmail className="text-2xl" />
                                <span>
                                    {isSubmitting 
                                        ? t('auth.processing') 
                                        : step === 'email'
                                            ? t('auth.continueWithEmail')
                                            : t('auth.login')
                                    }
                                </span>
                                {isSubmitting && (
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin ml-2"></div>
                                )}
                            </button>

                            {step === 'password' && (
                                <div className="text-sm text-center space-x-4">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setStep('email');
                                            setPassword('');
                                            setError('');
                                        }}
                                        className="text-blue-600 hover:text-blue-500"
                                    >
                                        {t('auth.useAnotherEmail')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => navigate('/forgot-password')}
                                        className="text-blue-600 hover:text-blue-500"
                                    >
                                        {t('auth.forgotPassword')}
                                    </button>
                                </div>
                            )}
                        </form>

                        <div className="text-center text-sm text-gray-600">
                            {t('auth.termsText')}{" "}
                            <a href="/terms" className="text-blue-600 hover:underline">
                                {t('auth.termsLink')}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;